import React from 'react';
import { Switch } from '@onward-delivery/design';
import { ChevronDown, ChevronUp, Icon } from '@onward-delivery/design/icons';
import { CARRIER_RELATIONSHIP_LABELS, CARRIER_STATUS, NOT_CONNECTED_LABEL } from './constants';
import RowActionsMenu from '../modals/RowActionsMenu';
import Pill from './Pill';
import { signsInOnOpen } from './utils';
import CarrierCell from './cells/CarrierCell';
import ContactCell from './cells/ContactCell';
import LegsCell from './cells/LegsCell';
import StatusCell from './cells/StatusCell';
import TmsCell from './cells/TmsCell';

const valueOrDash = ({ getValue }) => getValue() || '-';

// Fixed, so a type with no carriers yet still filters; Motor Carriers have no row of their own.
const TYPE_FILTER_OPTIONS = [CARRIER_RELATIONSHIP_LABELS.OWNED, CARRIER_RELATIONSHIP_LABELS.PARTNER].map((label) => ({
    label,
    value: label,
}));

const STATUS_FILTER_OPTIONS = Object.values(CARRIER_STATUS).map((label) => ({ label, value: label }));

const tmsValuesOf = (row) => (row.tmsTags.length ? row.tmsTags.map((tag) => tag.label) : [NOT_CONNECTED_LABEL]);
const airportValuesOf = (row) => row.airports || [];

const filterOptionsOf = (values) => {
    const labels = [...new Set(values)];
    labels.sort();

    return labels.map((label) => ({ label, value: label }));
};

// A row can hold several TMSs or airports, and the library's own filter compares the joined cell text.
const anyValueChecked = (valuesOf) => (row, columnId, checked) =>
    !checked?.length || valuesOf(row.original).some((value) => checked.includes(value));

// The library's header button sets font-medium, so the 11px bold has to sit on the label to win.
const headerLabel = (text) => () =>
    (
        <span className="inline-flex items-center gap-1">
            <span className="text-[11px] font-bold uppercase text-slate-500">{text}</span>
            <span className="text-[12px] font-medium text-slate-400">⁝</span>
        </span>
    );

export const carrierColumns = ({ onToggleActive, onOpen, onDetails, rows = [] } = {}) => {
    const tmsValues = rows.flatMap(tmsValuesOf);
    const airportValues = rows.flatMap(airportValuesOf);
    const tmsFilterOptions = filterOptionsOf(tmsValues);
    const airportFilterOptions = filterOptionsOf(airportValues);

    const columns = [
        {
            id: 'carrier',
            header: headerLabel('Carrier'),
            size: 304,
            minSize: 250,
            accessorFn: (row) => row.business_name,
            cell: CarrierCell,
        },
        {
            id: 'airports',
            header: headerLabel('Airports'),
            size: 101,
            // Joined here rather than in the cell so sorting reads the same string the column displays.
            accessorFn: (row) => (row.airports || []).join(', '),
            meta: { filter: { type: 'multi-select', options: airportFilterOptions } },
            filterFn: anyValueChecked(airportValuesOf),
            cell: valueOrDash,
        },
        {
            id: 'type',
            header: headerLabel('Type'),
            // Fits the 124px Fulfillment Partner pill.
            size: 144,
            accessorFn: (row) => row.type,
            meta: { filter: { type: 'multi-select', options: TYPE_FILTER_OPTIONS } },
            cell: ({ getValue }) => {
                const type = getValue();
                if (!type) return '-';

                return <Pill tone="type">{type}</Pill>;
            },
        },
        {
            id: 'tms',
            header: headerLabel('TMS'),
            size: 160,
            accessorFn: (row) => row.tmsTags.map((tag) => tag.label).join(', '),
            meta: { filter: { type: 'multi-select', options: tmsFilterOptions } },
            filterFn: anyValueChecked(tmsValuesOf),
            cell: TmsCell,
        },
        {
            id: 'contact',
            header: headerLabel('Contact'),
            size: 226,
            enableSorting: false,
            accessorFn: (row) => row.email,
            cell: ContactCell,
        },
        {
            id: 'legs',
            header: headerLabel('Legs'),
            // Two leg pills need 145px on one line.
            size: 172,
            enableSorting: false,
            accessorFn: (row) => (row.legs || []).join(', '),
            cell: LegsCell,
        },
        {
            id: 'status',
            header: headerLabel('Status'),
            size: 100,
            accessorFn: (row) => row.status,
            meta: { filter: { type: 'multi-select', options: STATUS_FILTER_OPTIONS } },
            cell: StatusCell,
        },
        {
            id: 'active',
            header: headerLabel('Active'),
            size: 110,
            enableSorting: false,
            cell: ({ row }) => {
                if (row.original.isMarketplace) return '-';

                const readOnlyReason = row.original.owned ? undefined : "Managed by the carrier's own account";

                return (
                    <span title={readOnlyReason}>
                        <Switch
                            checked={row.original.active}
                            disabled={!row.original.canToggleActive}
                            onCheckedChange={(next) => onToggleActive?.(row.original, next)}
                            aria-label={`Toggle ${row.original.business_name} active`}
                        />
                    </span>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            // 129px of buttons, the kebab included, plus this last column's 8px and 20px padding.
            size: 157,
            enableSorting: false,
            // The library's truncate wrapper clips the chevron's left border when fonts render Open wider.
            meta: { wrap: true },
            // Not the library's outline button: the design wants hairline borders and 12px semibold.
            cell: ({ row }) => {
                const isExpanded = row.getIsExpanded();
                const toggleIcon = isExpanded ? ChevronUp : ChevronDown;
                const toggleVerb = isExpanded ? 'Collapse' : 'Expand';
                const openButton = row.original.isMarketplace ? null : (
                    <button
                        type="button"
                        onClick={() => onOpen?.(row.original)}
                        className="rounded-[6px] border border-[#eceff4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-800"
                    >
                        Open
                    </button>
                );
                const actionsMenu = signsInOnOpen(row.original) ? (
                    <RowActionsMenu
                        items={[{ key: 'details', label: 'Details', onClick: () => onDetails?.(row.original) }]}
                    />
                ) : null;

                return (
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={row.getToggleExpandedHandler()}
                            aria-expanded={isExpanded}
                            aria-label={`${toggleVerb} ${row.original.business_name}`}
                            className="rounded-[6px] border border-slate-200 bg-white p-2 text-slate-500"
                        >
                            <Icon icon={toggleIcon} size="sm" className="size-3" />
                        </button>
                        {openButton}
                        {actionsMenu}
                    </div>
                );
            },
        },
    ];

    // isUtility is the library's only way to drop the reorder grip; its visibility-menu effect is unused here.
    return columns.map((column) => ({ ...column, meta: { ...column.meta, isUtility: true } }));
};
